import { memo, useContext, useCallback, useMemo } from 'react';
import { Plus as PlusIcon, ArrowRight, Settings as SettingsIcon } from 'react-feather';
import styled from '@emotion/styled';
import { ChatActionType, ChatContextType } from '@trinity/chat';
import {
  AuraChatContext,
  ChatViewType,
  ChatViewTypeContext,
  SideBarExpandedContext,
} from '@trinity/chat-components/components/BrandInsights/context';
import { SegmentChatContext } from '@trinity/chat-components/components/BrandInsights/SegmentChat.Context';
import {
  ActiveChatSessionContext,
  ChatPageContextDataContext,
  ChatSettingsToggleContext,
  SubmitActionContext,
} from '@trinity/chat-components/components/Chat/context';
import { useIsFullPageChat } from '@trinity/chat-components/components/Chat/hooks/useIsFullPageChat';
import {
  ChatHistory,
  SidebarArrowToggler,
  ChatHistoryItemsContext,
  ChatHistoryManagerContext,
  ChatHistorySharedContext,
} from '@trinity/chat-components/components/Chat.History/index.js';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { resetContextSubject } from '@trinity/chat-components/components/Chat.State/state.chat-session';
import { DigitalTwinsContext } from '@trinity/chat-components/components/DigitalTwin/context';
import { HistoryChatCount } from '@trinity/chat-components/components/presentation/History.ChatCount';
import { Constants } from '@trinity/chat-components/components/Theme';
import { ChatWelcomeScreenContext } from '@trinity/chat-components/containers/BrandInsights/Context.js';
import { useNavigate } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { RateLimitingContext } from '@trinity/chat-components/containers/RateLimiting/Context.js';
import { useActiveApplicationIdentifier } from '@trinity/chat-components/containers/UserApplications';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import AuraLogo from '@trinity/chat-components/static/images/aura-icon.svg?url';
import { shouldForwardProp } from '@trinity/chat-components/utils/emotion';
import { ChatApplication } from '@trinity/management-app-client';

export const ChatSidebar = () => {
  const pageContext = useContext(ChatPageContextDataContext);
  const chat = useContext(ActiveChatSessionContext);
  const [isExpanded, setIsExpanded] = useContext(SideBarExpandedContext);
  const items = useContext(ChatHistoryItemsContext);
  const shared = useContext(ChatHistorySharedContext);
  const historyManager = useContext(ChatHistoryManagerContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const isFullpageChat = useIsFullPageChat();
  const brandIdentifier = useActiveBrandIdentifier();
  const chatWelcomeScreen = useContext(ChatWelcomeScreenContext);
  const { query } = useContext(RateLimitingContext);
  const { auraChatStatus, setAuraChatStatus } = useContext(AuraChatContext);
  const activeApplicationIdentifier = useActiveApplicationIdentifier();
  const isDigitalTwinsMode = activeApplicationIdentifier === ChatApplication.DigitalTwins;
  const digitalTwins = useContext(DigitalTwinsContext);
  const { setSegmentChatStatus, setSegmentIconUrl } = useContext(SegmentChatContext);
  const submitAction = useContext(SubmitActionContext);
  const navigate = useNavigate();

  const handleChatWithSegments = useCallback(() => {
    setSegmentChatStatus(true);
    submitAction?.(
      { type: ChatActionType.ChatWithSegment, fromSidebar: true },
      `I'd like to chat with a segment.`
    );
  }, [submitAction, setSegmentChatStatus]);

  const chatViewType = useContext(ChatViewTypeContext);

  const isSegmentationProject = useMemo(() => {
    const contextType = chat?.context?.type;
    const metadata = chat?.context?.metadata as Record<string, unknown> | undefined;
    return (
      (contextType === ChatContextType.Project || contextType === ChatContextType.Agent) &&
      !!metadata?.isSegmentationProject
    );
  }, [chat?.context]);

  const showSegmentsButton = isSegmentationProject && chatViewType !== ChatViewType.Page;
  const showAuraButton = digitalTwins.available;

  const [settings, toggleSettings] = useContext(ChatSettingsToggleContext);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, [setIsExpanded]);

  const handleNewChat = useCallback(() => {
    setAuraChatStatus(false);
    setSegmentChatStatus(false);
    setSegmentIconUrl(undefined);
    dispatch({ type: 'filters/updated', payload: { filters: { tags: [] } } });
    chatWelcomeScreen?.dismiss();
    if (settings) {
      toggleSettings();
    }

    const context = pageContext ?? chat?.context;

    const resetLandingCards =
      context.type === ChatContextType.Agent && context.data.showLandingCardsOnNewChat;

    dispatch({
      type: 'new-chat-started',
      payload: {
        context: resetContextSubject(context, resetLandingCards),
        brandIdentifier,
        resetLandingCards,
      },
    });
  }, [
    setAuraChatStatus,
    setSegmentChatStatus,
    setSegmentIconUrl,
    chatWelcomeScreen,
    settings,
    toggleSettings,
    dispatch,
    chat,
    pageContext,
    brandIdentifier,
  ]);

  const _handleNewAuraChat = useCallback(() => {
    setAuraChatStatus(true);
  }, [setAuraChatStatus]);

  const _backToChat = useCallback(() => {
    setAuraChatStatus(false);
  }, [setAuraChatStatus]);

  const handleLaunchAuraDigitalTwins = useCallback(() => {
    if (isDigitalTwinsMode) {
      handleNewChat();
    }
    navigate(digitalTwins.basePath);
  }, [isDigitalTwinsMode, handleNewChat, navigate, digitalTwins.basePath]);

  const historyCount = useMemo(() => items.length + shared.length, [items.length, shared.length]);

  const isRateLimitExceeded = useMemo(() => {
    return query?.data?.allowed === false;
  }, [query?.data?.allowed]);

  return (
    <Root>
      <Wrap isExpanded={isExpanded} isFullPageChat={isFullpageChat}>
        <Content>
          <Collapsed isExpanded={isExpanded}>
            <CollapsedHeader>
              <NewChatButtonCollapsed disabled={isRateLimitExceeded} onClick={handleNewChat}>
                <StyledCollapsedPlusIcon />
              </NewChatButtonCollapsed>
            </CollapsedHeader>

            {showSegmentsButton && (
              <SegmentsButtonCollapsed onClick={handleChatWithSegments} title="Chat with Segments">
                <img src={AuraLogo} alt="Chat with Segments" />
              </SegmentsButtonCollapsed>
            )}
            {showAuraButton && (
              <SegmentsButtonCollapsed onClick={handleLaunchAuraDigitalTwins} title="Launch Aura">
                <img src={AuraLogo} alt="Launch Aura" />
              </SegmentsButtonCollapsed>
            )}
            <CountWrapper>
              {!historyManager.loading && !auraChatStatus && (
                <StyledHistoryChatCount onClick={toggleExpanded} count={historyCount} />
              )}
            </CountWrapper>
          </Collapsed>
          <Expanded isExpanded={isExpanded}>
            {isFullpageChat && (
              <ExpandedHeader>
                <Text>Conversations</Text>
                <NewChatButtonCollapsed
                  disabled={isRateLimitExceeded}
                  isFullPageChat={isFullpageChat}
                  onClick={handleNewChat}
                >
                  <StyledCollapsedPlusIcon />
                </NewChatButtonCollapsed>
              </ExpandedHeader>
            )}

            {!isFullpageChat && (
              <ExpandedHeader>
                <NewChatButtonExpanded disabled={isRateLimitExceeded} onClick={handleNewChat}>
                  <StyledExpandedPlusIcon />
                  <NewChatTextExpanded>New Chat</NewChatTextExpanded>
                </NewChatButtonExpanded>
              </ExpandedHeader>
            )}

            {showSegmentsButton && (
              <SegmentsButtonExpanded onClick={handleChatWithSegments}>
                <img src={AuraLogo} />
                <span className="text">Chat with Segments</span>
                <ArrowRight size={24} />
              </SegmentsButtonExpanded>
            )}

            {showAuraButton && (
              <SegmentsButtonExpanded onClick={handleLaunchAuraDigitalTwins}>
                <img src={AuraLogo} alt="Launch Aura" />
                <span className="text">Launch Aura</span>
                <ArrowRight size={30} />
              </SegmentsButtonExpanded>
            )}

            {/* <ChatHistorySearch /> */}

            {!auraChatStatus && <ChatHistory items={items} shared={shared} />}
          </Expanded>
        </Content>

        <StyledToggler open={isExpanded} onClick={toggleExpanded} />
        <Settings onClick={toggleSettings}>
          <SettingsIcon />
        </Settings>
      </Wrap>
    </Root>
  );
};

const Root = styled.div`
  box-sizing: border-box;

  border-top-left-radius: ${Constants.Modal.BorderRadius}px;
  border-bottom-left-radius: ${Constants.Modal.BorderRadius}px;

  padding: 15px 0;
  height: 100%;
  background: ${({ theme }) => theme.palette.gray.light2};
`;

const Settings = styled.div`
  position: absolute;
  bottom: 0;
  left: 20px;
  background-color: ${({ theme }) => theme.palette.gray.light1};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 34px;
  height: 34px;
  cursor: pointer;
`;

export const EmptySidebar = memo(() => (
  <Root>
    <Wrap />
  </Root>
));

const Wrap = styled('div', { shouldForwardProp: shouldForwardProp('isFullPageChat') })<BarProps>`
  position: relative;
  width: ${(props) => {
    if (props.isExpanded && props.isFullPageChat) return '350';
    if (props.isExpanded) return Constants.Sidebar.ExpandedWidth;
    return Constants.Sidebar.Width;
  }}px;
  transition: width 500ms;
  height: 100%;
  background: #f2f2f2;
`;

const Text = styled.div`
  margin-right: auto;
  font-size: 28px;
  font-family: ${({ theme }) => theme.fonts.bold};
  margin-bottom: 10px;
`;

const Content = styled.div`
  box-sizing: border-box;

  position: relative;

  display: flex;
  flex-direction: column;
  align-items: center;

  overflow: hidden;

  height: 100%;
`;

const Expanded = styled.div<BarProps>`
  display: ${(props) => (props.isExpanded ? 'flex' : 'none')};
  flex-direction: column;
  align-items: center;

  opacity: ${(props) => (props.isExpanded ? 1 : 0)};
  transition: opacity 400ms;
  position: absolute;

  height: 100%;
  width: 100%;
`;

const Collapsed = styled.div<BarProps>`
  display: ${(props) => (props.isExpanded ? 'none' : 'flex')};
  flex-direction: column;
  align-items: center;

  opacity: ${(props) => (props.isExpanded ? 0 : 1)};
  transition: opacity 400ms;
  position: absolute;

  height: 100%;
  width: 100%;
`;

const ExpandedHeader = styled.div`
  box-sizing: border-box;
  display: flex;
  justify-content: flex-end;
  width: 100%;
  padding: 0 ${Constants.Sidebar.HorizontalPadding}px;
  margin-bottom: 15px;
  align-items: center;
`;

const CollapsedHeader = styled.div`
  margin-bottom: 15px;
`;

const StyledCollapsedPlusIcon = styled(PlusIcon)`
  color: ${(props) => props.theme.palette.white.main};
  stroke-width: 3;
  width: 40;
  min-width: 40;
`;

const StyledExpandedPlusIcon = styled(PlusIcon)`
  color: ${(props) => props.theme.palette.secondary.main};
  stroke-width: 3;
  width: 24;
  min-width: 24;
`;
const NewChatButtonCollapsed = styled('button', {
  shouldForwardProp: shouldForwardProp('isFullPageChat'),
})<BarProps>`
  margin-right: ${(props) => (props.isFullPageChat ? '5px' : '0px')};
  cursor: pointer;
  box-sizing: border-box;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.sentiment.primary.main};
  width: ${Constants.Sidebar.IconSize}px;
  height: ${Constants.Sidebar.IconSize}px;
  padding: 10px;

  display: flex;
  align-items: center;
  justify-content: center;

  transition: background-color 0.3s;

  &:hover {
    background-color: ${(props) => props.theme.palette.sentiment.primary.dark};
  }
  &:disabled {
    background-color: ${(props) => props.theme.palette.gray.light1};
    cursor: not-allowed;
  }
`;

const NewChatButtonExpanded = styled.button`
  border-radius: 25px;
  background-color: ${({ theme }) => theme.palette.sentiment.primary.main};
  padding: 10px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  flex: 1;

  &:disabled {
    background-color: ${(props) => props.theme.palette.gray.light1};
    cursor: not-allowed;
  }
`;

const NewChatTextExpanded = styled.div`
  color: ${(props) => props.theme.palette.secondary.main};
  font-family: ${(props) => props.theme.fonts.semiBold};

  display: flex;
  justify-content: center;
  align-items: center;

  flex: 1;
`;

const CountWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  width: ${Constants.Sidebar.IconSize}px;
  height: ${Constants.Sidebar.IconSize}px;
  box-sizing: border-box;
  margin-top: 13px;
`;

const StyledHistoryChatCount = styled(HistoryChatCount)`
  cursor: pointer;
`;

const StyledToggler = styled(SidebarArrowToggler)`
  position: absolute;
  top: 50%;
  transform: translate(0, -50%);
  right: -${Constants.Sidebar.ArrowTogglerSize / 2}px;
  z-index: ${(props) => props.theme.zIndex.popper};
`;

type BarProps = {
  isExpanded?: boolean;
  isFullPageChat?: boolean;
};

const SegmentsButtonCollapsed = styled.button`
  margin-top: 10px;
  margin-bottom: 10px;
  padding: 10px;
  border-radius: 50%;
  background: linear-gradient(90deg, #4f35e6 0%, #28aae1 100%);
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7));
  width: ${Constants.Sidebar.IconSize}px;
  height: ${Constants.Sidebar.IconSize}px;
  box-shadow: inset 0 1px 3px 0 #1a113b;

  &:hover {
    transform: scale(1.04);
  }

  img {
    width: 25px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
    margin-left: 1px;
  }
`;

const SegmentsButtonExpanded = styled.button`
  touch-action: manipulation;
  -webkit-appearance: none;
  appearance: none;
  vertical-align: middle;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 15px;
  padding: 12px 16px;
  background: linear-gradient(90deg, #4f35e6 0%, #28aae1 100%);
  color: ${({ theme }) => theme.palette.white.main};
  cursor: pointer;
  width: calc(100% - 27px);
  height: 60px;
  margin: 0 ${Constants.Sidebar.ItemHorizontalPadding}px;
  margin-bottom: 20px;
  transition-property: transform, background-color, filter;
  transition-duration: 0.2s;
  transition-timing-function: cubic-bezier(0.55, 1, 0.15, 1);
  box-sizing: border-box;
  border: none;
  box-shadow: inset 0 1px 3px 0 #1a113b;

  &:hover {
    transform: scale(1.04);
    filter: brightness(1.1);
  }

  &:focus {
    outline-style: solid;
    outline-color: transparent;
  }

  &:focus-visible {
    transform: scale(1.04);
    filter: brightness(1.1);
    text-decoration-line: underline;
    text-decoration-thickness: 0.0625em;
    text-underline-offset: 0.125em;
  }

  &:active {
    transform: scale(0.96);
    filter: brightness(0.9);
  }

  img {
    width: 32px;
    height: 32px;
    margin-right: 10px;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7));
  }

  .text {
    flex: 1;
    text-align: left;
    font-family: ${({ theme }) => theme.fonts.semiBold};
    font-size: 16px;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7));
  }

  .arrow-icon,
  .arrowright,
  svg {
    margin-right: -5px;
    color: ${({ theme }) => theme.palette.white.main};
    font-size: 14px;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7));
  }
`;
